
// Deployment trigger: 2026-04-24
import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Plus, Trash2, Gamepad, Gift, Layers, Code, Play, AlertCircle, Brain, GripVertical, Calendar, Trophy, ChevronUp, ChevronDown, ImagePlus, Loader2, CheckCircle2 } from 'lucide-react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { Campaign, Prize, GameType, GlobalConfig } from '../types';
import { GAME_TEMPLATES, DEFAULT_QUESTIONS, DEFAULT_ENABLED_GAMES } from '../constants';
import GameSimulator from './GameSimulator';
import EmbedCode from './EmbedCode';

interface CampaignEditorProps {
  campaign: Campaign;
  onSave: (c: Campaign) => void;
  onAutoSave?: (c: Campaign) => Promise<void>;
  onCancel: () => void;
  domain?: string;
  globalConfig?: GlobalConfig;
}

const compressImage = (file: File, quality: number): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }
        ctx.drawImage(img, 0, 0);
        canvas.toBlob(
          (blob) => {
            if (blob) resolve(blob);
            else reject(new Error('Canvas to Blob failed'));
          },
          'image/jpeg',
          quality / 100
        );
      };
      img.onerror = (error) => reject(error);
    };
    reader.onerror = (error) => reject(error);
  });
};

const CampaignEditor: React.FC<CampaignEditorProps> = ({ campaign, onSave, onAutoSave, onCancel, domain, globalConfig }) => {
  const [activeTab, setActiveTab] = useState<'settings' | 'prizes' | 'questions' | 'preview' | 'embed'>('settings');
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  
  // Default to 1 month from today for Raffle
  const defaultRaffleDate = useMemo(() => {
    const d = new Date();
    d.setMonth(d.getMonth() + 1);
    return d.toISOString().split('T')[0];
  }, []);

  const [formData, setFormData] = useState<Campaign>({
    ...campaign, 
    questions: campaign.questions || (campaign.type === GameType.TRIVIA ? [...DEFAULT_QUESTIONS] : []),
    endDate: campaign.endDate || defaultRaffleDate,
    winnerCount: campaign.winnerCount || 1
  });

  // Sync ID if it gets updated (e.g., after first auto-save of a new campaign)
  useEffect(() => {
    if (campaign.id && !formData.id) {
      setFormData(prev => ({ ...prev, id: campaign.id }));
    }
  }, [campaign.id, formData.id]);

  // Keep track of the latest onAutoSave function to avoid re-triggering the effect
  const onAutoSaveRef = useRef(onAutoSave);
  useEffect(() => {
    onAutoSaveRef.current = onAutoSave;
  }, [onAutoSave]);

  // Auto-save effect
  const initialMount = useRef(true);
  useEffect(() => {
    if (initialMount.current) {
      initialMount.current = false;
      return;
    }

    const timer = setTimeout(async () => {
      if (!onAutoSaveRef.current) return;
      setSaveStatus('saving');
      try {
        await onAutoSaveRef.current(formData);
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus('idle'), 2000);
      } catch (err) {
        console.error("Auto-save failed:", err);
        setSaveStatus('idle');
      }
    }, 1000); // 1 second debounce

    return () => clearTimeout(timer);
  }, [formData]); // Only depend on formData

  const updatePrize = (index: number, updates: Partial<Prize>) => {
    const newPrizes = [...formData.prizes];
    newPrizes[index] = { ...newPrizes[index], ...updates };
    setFormData({ ...formData, prizes: newPrizes });
  };

  const addPrize = () => {
    const newPrize: Prize = {
      id: Date.now().toString(),
      name: 'New Prize',
      type: 'coupon',
      value: 'CODE',
      probability: 10,
      color: '#'+Math.floor(Math.random()*16777215).toString(16)
    };
    setFormData({ ...formData, prizes: [...formData.prizes, newPrize] });
  };

  const removePrize = (id: string) => {
    setFormData({ ...formData, prizes: formData.prizes.filter(p => p.id !== id) });
  };

  const [uploadingImageIdx, setUploadingImageIdx] = useState<number | null>(null);

  const handleImageUpload = async (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingImageIdx(index);
    try {
      const quality = globalConfig?.imageCompression ?? 80;
      const compressedBlob = await compressImage(file, quality);
      
      const imgData = new FormData();
      imgData.append('image', compressedBlob, file.name.replace(/\.[^/.]+$/, "") + ".jpg");
      
      const res = await fetch(`/api/upload-image`, {
        method: 'POST',
        body: imgData
      });
      
      const data = await res.json();
      if (data.success) {
        updatePrize(index, { imageUrl: data.data.url });
      } else {
        alert("Failed to upload image: " + (data.error?.message || data.error || "Unknown error"));
      }
    } catch (err) {
      console.error("Image upload error:", err);
      alert("An error occurred while uploading the image.");
    } finally {
      setUploadingImageIdx(null);
    }
  };

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const items = Array.from(formData.prizes);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    setFormData({ ...formData, prizes: items });
  };

  const totalWeight = useMemo(() => 
    formData.prizes.reduce((sum, p) => sum + (Number(p.probability) || 0), 0)
  , [formData.prizes]);

  const isValid = formData.type === GameType.RAFFLE ? true : (totalWeight <= 100 && formData.prizes.every(p => p.probability >= 0));

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-xl overflow-hidden flex flex-col min-h-[600px]">
      <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50 overflow-hidden">
        <div className="flex-1 overflow-x-auto flex scrollbar-hide">
          <TabButton 
            active={activeTab === 'settings'} 
            onClick={() => setActiveTab('settings')}
            icon={<Gamepad className="w-4 h-4" />}
            label="Setup"
          />
          <TabButton 
            active={activeTab === 'prizes'} 
            onClick={() => setActiveTab('prizes')}
            icon={<Gift className="w-4 h-4" />}
            label={formData.type === GameType.RAFFLE ? "Draw Prizes" : "Prizes"}
          />
          {formData.type === GameType.TRIVIA && (
            <TabButton 
              active={activeTab === 'questions'} 
              onClick={() => setActiveTab('questions')}
              icon={<Brain className="w-4 h-4" />}
              label="Quiz"
            />
          )}
          <TabButton 
            active={activeTab === 'preview'} 
            onClick={() => setActiveTab('preview')}
            icon={<Play className="w-4 h-4" />}
            label="Test"
          />
          <TabButton 
            active={activeTab === 'embed'} 
            onClick={() => setActiveTab('embed')}
            icon={<Code className="w-4 h-4" />}
            label="Embed"
          />
        </div>
        <div className="flex gap-2 px-4 sm:px-6 shrink-0 items-center">
          {saveStatus === 'saving' && (
            <span className="text-[10px] sm:text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-1 mr-1 sm:mr-2">
              <Loader2 className="w-3 h-3 animate-spin" /> <span className="hidden sm:inline">Saving...</span>
            </span>
          )}
          {saveStatus === 'saved' && (
            <span className="text-[10px] sm:text-xs font-bold text-emerald-500 uppercase tracking-widest flex items-center gap-1 mr-1 sm:mr-2">
              <CheckCircle2 className="w-3 h-3" /> <span className="hidden sm:inline">Saved</span>
            </span>
          )}
          <button onClick={onCancel} className="px-3 sm:px-4 py-2 text-slate-500 hover:text-slate-800 text-xs sm:text-sm font-bold uppercase tracking-widest transition-colors">Cancel</button>
          <button 
            disabled={!isValid || saveStatus === 'saving'}
            onClick={() => onSave(formData)} 
            className={`px-4 sm:px-8 py-2 sm:py-2.5 rounded-xl text-xs sm:text-sm font-black shadow-lg transition-all uppercase tracking-widest ${isValid && saveStatus !== 'saving' ? 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-indigo-100 hover:-translate-y-0.5' : 'bg-slate-200 text-slate-400 cursor-not-allowed'}`}
          >
            Save
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 sm:p-8">
        {activeTab === 'settings' && (
          <div className="max-w-4xl mx-auto space-y-10">
            <div className="space-y-6">
              <label className="block">
                <span className="text-slate-700 font-bold text-xs block mb-2 uppercase tracking-widest">Campaign Title</span>
                <input 
                  type="text" 
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-6 py-5 rounded-2xl border border-slate-200 bg-white text-slate-800 placeholder:text-slate-300 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 outline-none transition-all text-base font-bold shadow-sm"
                  placeholder="e.g. Grand Holiday Giveaway"
                />
              </label>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-slate-700 font-bold text-xs block mb-2 uppercase tracking-widest">
                    {formData.type === GameType.RAFFLE ? 'Draw Date (Same as Expiration Date)' : 'Expiration Date'}
                  </label>
                  <div className="relative group">
                    <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-hover:text-indigo-600 transition-colors pointer-events-none" />
                    <input 
                      type="date" 
                      value={formData.endDate || ''}
                      onChange={(e) => setFormData({...formData, endDate: e.target.value})}
                      onClick={(e) => (e.currentTarget as any).showPicker?.()}
                      className="w-full pl-12 pr-6 py-4 rounded-2xl border border-slate-200 bg-white text-slate-800 outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all font-bold cursor-pointer shadow-sm"
                    />
                  </div>
                  <p className="text-[10px] text-slate-500 font-medium mt-2">
                    {formData.type === GameType.RAFFLE 
                      ? "The date you plan to draw the winners. The game will automatically expire after this date." 
                      : "The game will automatically become inactive and unplayable after this date."}
                  </p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-10">
                <div className="space-y-4 order-2 md:order-1">
                  <span className="text-slate-700 font-bold text-xs block uppercase tracking-widest">Select Game Engine</span>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {GAME_TEMPLATES
                      .filter(temp => (globalConfig?.enabledGames ?? DEFAULT_ENABLED_GAMES).includes(temp.id) || temp.id === formData.type)
                      .map(temp => (
                      <button 
                        key={temp.id}
                        onClick={() => {
                          const newType = temp.id;
                          const isNewCampaign = !formData.id;
                          let newPrizes = formData.prizes;
                          
                          if (isNewCampaign) {
                            if (newType === GameType.RAFFLE) {
                              newPrizes = globalConfig?.defaultRafflePrizes || [];
                            } else {
                              newPrizes = globalConfig?.defaultPrizes || [];
                            }
                          }

                          setFormData({
                            ...formData, 
                            type: newType,
                            prizes: newPrizes,
                            questions: newType === GameType.TRIVIA ? (formData.questions?.length ? formData.questions : [...DEFAULT_QUESTIONS]) : formData.questions
                          });
                        }}
                        className={`text-left p-5 rounded-2xl border-2 transition-all ${formData.type === temp.id ? 'border-indigo-500 bg-indigo-50/50 shadow-md ring-4 ring-indigo-50' : 'border-slate-100 bg-white hover:border-slate-200 hover:bg-slate-50'}`}
                      >
                        <div className={`font-black text-xs mb-2 uppercase tracking-tight ${formData.type === temp.id ? 'text-indigo-700' : 'text-slate-700'}`}>{temp.name}</div>
                        <div className="text-[10px] text-slate-400 font-medium leading-relaxed line-clamp-2">{temp.description}</div>
                      </button>
                    ))}
                  </div>
                </div>
                
                <div className="space-y-6 order-1 md:order-2">
                  {formData.type === GameType.SPIN_WHEEL && (
                    <div className="space-y-6">
                      <div className="space-y-4">
                        <span className="text-slate-700 font-bold text-xs block uppercase tracking-widest">Button Text</span>
                        <input 
                          type="text" 
                          value={formData.buttonText || 'SPIN'}
                          onChange={(e) => setFormData({...formData, buttonText: e.target.value})}
                          className="w-full px-6 py-4 rounded-2xl border border-slate-200 bg-white text-slate-800 placeholder:text-slate-300 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 outline-none transition-all text-sm font-bold shadow-sm"
                          placeholder="e.g. SPIN"
                        />
                      </div>
                      <div className="space-y-4">
                        <span className="text-slate-700 font-bold text-xs block uppercase tracking-widest">Select Theme</span>
                        <div className="grid grid-cols-2 gap-4">
                          <button 
                            onClick={() => setFormData({...formData, theme: 'casino'})}
                            className={`text-left p-4 rounded-2xl border-2 transition-all ${formData.theme === 'casino' || !formData.theme ? 'border-indigo-500 bg-indigo-50/50 shadow-md' : 'border-slate-100 bg-white hover:border-slate-200'}`}
                          >
                            <div className={`font-black text-xs uppercase tracking-tight ${formData.theme === 'casino' || !formData.theme ? 'text-indigo-700' : 'text-slate-700'}`}>Casino Royale</div>
                          </button>
                          <button 
                            onClick={() => setFormData({...formData, theme: 'carnival'})}
                            className={`text-left p-4 rounded-2xl border-2 transition-all ${formData.theme === 'carnival' ? 'border-indigo-500 bg-indigo-50/50 shadow-md' : 'border-slate-100 bg-white hover:border-slate-200'}`}
                          >
                            <div className={`font-black text-xs uppercase tracking-tight ${formData.theme === 'carnival' ? 'text-indigo-700' : 'text-slate-700'}`}>Lucky Carnival</div>
                          </button>
                          <button 
                            onClick={() => setFormData({...formData, theme: 'neon'})}
                            className={`text-left p-4 rounded-2xl border-2 transition-all ${formData.theme === 'neon' ? 'border-indigo-500 bg-indigo-50/50 shadow-md' : 'border-slate-100 bg-white hover:border-slate-200'}`}
                          >
                            <div className={`font-black text-xs uppercase tracking-tight ${formData.theme === 'neon' ? 'text-indigo-700' : 'text-slate-700'}`}>Neon Cyberpunk</div>
                          </button>
                          <button 
                            onClick={() => setFormData({...formData, theme: 'minimal'})}
                            className={`text-left p-4 rounded-2xl border-2 transition-all ${formData.theme === 'minimal' ? 'border-indigo-500 bg-indigo-50/50 shadow-md' : 'border-slate-100 bg-white hover:border-slate-200'}`}
                          >
                            <div className={`font-black text-xs uppercase tracking-tight ${formData.theme === 'minimal' ? 'text-indigo-700' : 'text-slate-700'}`}>Minimalist Clean</div>
                          </button>
                          <button 
                            onClick={() => setFormData({...formData, theme: 'retro'})}
                            className={`text-left p-4 rounded-2xl border-2 transition-all ${formData.theme === 'retro' ? 'border-indigo-500 bg-indigo-50/50 shadow-md' : 'border-slate-100 bg-white hover:border-slate-200'}`}
                          >
                            <div className={`font-black text-xs uppercase tracking-tight ${formData.theme === 'retro' ? 'text-indigo-700' : 'text-slate-700'}`}>Retro Arcade</div>
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  {formData.type === GameType.MYSTERY_BOX && (
                    <div className="space-y-4">
                      <span className="text-slate-700 font-bold text-xs block uppercase tracking-widest">Select Theme</span>
                      <div className="grid grid-cols-2 gap-4">
                        <button 
                          onClick={() => setFormData({...formData, theme: 'classic'})}
                          className={`text-left p-4 rounded-2xl border-2 transition-all ${formData.theme === 'classic' || !formData.theme ? 'border-indigo-500 bg-indigo-50/50 shadow-md' : 'border-slate-100 bg-white hover:border-slate-200'}`}
                        >
                          <div className={`font-black text-xs uppercase tracking-tight ${formData.theme === 'classic' || !formData.theme ? 'text-indigo-700' : 'text-slate-700'}`}>Classic Red</div>
                        </button>
                        <button 
                          onClick={() => setFormData({...formData, theme: 'neon'})}
                          className={`text-left p-4 rounded-2xl border-2 transition-all ${formData.theme === 'neon' ? 'border-indigo-500 bg-indigo-50/50 shadow-md' : 'border-slate-100 bg-white hover:border-slate-200'}`}
                        >
                          <div className={`font-black text-xs uppercase tracking-tight ${formData.theme === 'neon' ? 'text-indigo-700' : 'text-slate-700'}`}>Neon Cyberpunk</div>
                        </button>
                        <button 
                          onClick={() => setFormData({...formData, theme: 'luxury'})}
                          className={`text-left p-4 rounded-2xl border-2 transition-all ${formData.theme === 'luxury' ? 'border-indigo-500 bg-indigo-50/50 shadow-md' : 'border-slate-100 bg-white hover:border-slate-200'}`}
                        >
                          <div className={`font-black text-xs uppercase tracking-tight ${formData.theme === 'luxury' ? 'text-indigo-700' : 'text-slate-700'}`}>Luxury Gold</div>
                        </button>
                      </div>
                    </div>
                  )}
                  {formData.type === GameType.SLOT_MACHINE && (
                    <div className="space-y-4">
                      <span className="text-slate-700 font-bold text-xs block uppercase tracking-widest">Select Theme</span>
                      <div className="grid grid-cols-2 gap-4">
                        <button 
                          onClick={() => setFormData({...formData, theme: 'casino'})}
                          className={`text-left p-4 rounded-2xl border-2 transition-all ${formData.theme === 'casino' || !formData.theme ? 'border-indigo-500 bg-indigo-50/50 shadow-md' : 'border-slate-100 bg-white hover:border-slate-200'}`}
                        >
                          <div className={`font-black text-xs uppercase tracking-tight ${formData.theme === 'casino' || !formData.theme ? 'text-indigo-700' : 'text-slate-700'}`}>Casino Royale</div>
                        </button>
                        <button 
                          onClick={() => setFormData({...formData, theme: 'carnival'})}
                          className={`text-left p-4 rounded-2xl border-2 transition-all ${formData.theme === 'carnival' ? 'border-indigo-500 bg-indigo-50/50 shadow-md' : 'border-slate-100 bg-white hover:border-slate-200'}`}
                        >
                          <div className={`font-black text-xs uppercase tracking-tight ${formData.theme === 'carnival' ? 'text-indigo-700' : 'text-slate-700'}`}>Lucky Carnival</div>
                        </button>
                        <button 
                          onClick={() => setFormData({...formData, theme: 'neon'})}
                          className={`text-left p-4 rounded-2xl border-2 transition-all ${formData.theme === 'neon' ? 'border-indigo-500 bg-indigo-50/50 shadow-md' : 'border-slate-100 bg-white hover:border-slate-200'}`}
                        >
                          <div className={`font-black text-xs uppercase tracking-tight ${formData.theme === 'neon' ? 'text-indigo-700' : 'text-slate-700'}`}>Neon Cyberpunk</div>
                        </button>
                        <button 
                          onClick={() => setFormData({...formData, theme: 'retro'})}
                          className={`text-left p-4 rounded-2xl border-2 transition-all ${formData.theme === 'retro' ? 'border-indigo-500 bg-indigo-50/50 shadow-md' : 'border-slate-100 bg-white hover:border-slate-200'}`}
                        >
                          <div className={`font-black text-xs uppercase tracking-tight ${formData.theme === 'retro' ? 'text-indigo-700' : 'text-slate-700'}`}>Retro Arcade</div>
                        </button>
                      </div>
                    </div>
                  )}
                  <div className="p-8 bg-slate-900 rounded-[32px] text-white shadow-2xl relative overflow-hidden group">
                    <div className="relative z-10">
                      <h4 className="font-bold text-sm mb-3 flex items-center gap-2 text-indigo-400">
                        <Layers className="w-4 h-4" /> Pro Tip
                      </h4>
                      <p className="text-xs text-slate-400 leading-relaxed font-medium">
                        {formData.type === GameType.RAFFLE ? "Raffles are best for high-value items. We display the whole prize pool to maximize entry rates!" : "Instant games are great for quick engagement and discount coupons."}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 p-6 border-2 border-slate-50 rounded-[32px] bg-slate-50/30 hover:bg-slate-50 transition-all cursor-pointer group">
                    <input 
                      type="checkbox" 
                      id="active-check"
                      checked={formData.active}
                      onChange={(e) => setFormData({...formData, active: e.target.checked})}
                      className="w-6 h-6 text-indigo-600 rounded-lg border-slate-300 focus:ring-indigo-500 cursor-pointer shadow-sm"
                    />
                    <label htmlFor="active-check" className="font-bold text-slate-700 text-sm cursor-pointer select-none">Live Status</label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'prizes' && (
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-4 mb-10">
              <div>
                <h3 className="text-2xl font-black text-[#0b0f1a] tracking-tight">Prize Logic</h3>
                <p className={`text-[10px] font-black uppercase tracking-widest mt-1 ${totalWeight > 100 ? 'text-red-500' : 'text-[#64748b]'}`}>
                  WEIGHT TOTAL: {totalWeight} (IDEAL: 100)
                </p>
              </div>
              <button 
                onClick={addPrize}
                className="flex items-center gap-2 bg-[#0b0f1a] text-white px-6 py-4 rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-slate-800 transition-all shadow-xl"
              >
                <Plus className="w-4 h-4" /> ADD ITEM
              </button>
            </div>

            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="prizes">
                {(provided) => (
                  <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-5">
                    {formData.prizes.map((prize, idx) => (
                      <Draggable key={prize.id} draggableId={prize.id} index={idx}>
                        {(provided, snapshot) => (
                          <div 
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className={`bg-white p-4 sm:p-6 rounded-[32px] border transition-all flex flex-col lg:flex-row items-stretch lg:items-center gap-6 relative group ${snapshot.isDragging ? 'shadow-2xl border-indigo-400 z-50 scale-[1.02]' : 'border-slate-100 shadow-sm'}`}
                          >
                            <div className="flex items-center gap-4 flex-1">
                              <div {...provided.dragHandleProps} className="p-2 text-slate-300 hover:text-slate-500 cursor-grab active:cursor-grabbing">
                                <GripVertical className="w-5 h-5" />
                              </div>
                              <div 
                                className="w-12 h-12 rounded-full flex items-center justify-center font-black text-white text-base shrink-0 shadow-lg relative"
                                style={{ backgroundColor: prize.color }}
                              >
                                {idx + 1}
                                <div className="absolute inset-0 rounded-full shadow-[inset_0_2px_4px_rgba(255,255,255,0.3)] pointer-events-none" />
                              </div>
                              <div className="flex-1">
                                <input 
                                  type="text" 
                                  value={prize.name}
                                  onChange={(e) => updatePrize(idx, { name: e.target.value })}
                                  className="w-full bg-transparent px-2 py-1.5 font-black text-[#0b0f1a] text-xl focus:outline-none border-b-2 border-transparent focus:border-indigo-500 transition-all"
                                  placeholder="Enter Prize Name..."
                                />
                              </div>
                            </div>

                            <div className="grid grid-cols-2 lg:flex lg:flex-[2] gap-6 items-end">
                              {formData.type === GameType.RAFFLE && (
                                <div className="flex-1 min-w-[120px]">
                                  <label className="text-[10px] uppercase font-black text-[#64748b] block mb-2 tracking-widest flex items-center justify-between">
                                    <span>Image</span>
                                  </label>
                                  <div className="relative h-[46px] w-full rounded-xl border border-[#e2e8f0] bg-[#f8f9fc] hover:border-[#cbd5e1] transition-colors overflow-hidden flex items-center justify-center">
                                    {uploadingImageIdx === idx ? (
                                      <Loader2 className="w-5 h-5 animate-spin text-indigo-500" />
                                    ) : prize.imageUrl ? (
                                      <div className="relative w-full h-full group/img">
                                        <img src={prize.imageUrl} alt="Prize" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover/img:opacity-100 transition-opacity flex items-center justify-center">
                                          <span className="text-white text-[10px] font-bold">Change</span>
                                        </div>
                                        <input 
                                          type="file" 
                                          accept="image/*"
                                          onChange={(e) => handleImageUpload(idx, e)}
                                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                        />
                                      </div>
                                    ) : (
                                      <>
                                        <ImagePlus className="w-5 h-5 text-slate-400" />
                                        <input 
                                          type="file" 
                                          accept="image/*"
                                          onChange={(e) => handleImageUpload(idx, e)}
                                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                        />
                                      </>
                                    )}
                                  </div>
                                </div>
                              )}
                              
                              {formData.type !== GameType.RAFFLE ? (
                                <div className="w-24">
                                  <label className="text-[10px] uppercase font-black text-[#64748b] block mb-2 tracking-widest">WEIGHT</label>
                                  <div className="relative group">
                                    <input 
                                      type="number" 
                                      value={prize.probability}
                                      onChange={(e) => updatePrize(idx, { probability: Number(e.target.value) })}
                                      className="w-full bg-[#f8f9fc] px-4 py-3.5 rounded-xl border border-[#e2e8f0] text-[12px] font-black text-indigo-600 outline-none text-center shadow-sm group-hover:border-[#cbd5e1] transition-colors"
                                    />
                                    <div className="absolute right-2 top-1/2 -translate-y-1/2 flex flex-col text-[#94a3b8]">
                                      <button onClick={() => updatePrize(idx, { probability: prize.probability + 1 })} className="hover:text-indigo-600"><ChevronUp className="w-3 h-3" /></button>
                                      <button onClick={() => updatePrize(idx, { probability: Math.max(0, prize.probability - 1) })} className="hover:text-indigo-600"><ChevronDown className="w-3 h-3" /></button>
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <div className="w-24">
                                  <label className="text-[10px] uppercase font-black text-[#64748b] block mb-2 tracking-widest">WINNERS</label>
                                  <div className="relative group">
                                    <input 
                                      type="number" 
                                      min="1"
                                      value={prize.winnersCount || 1}
                                      onChange={(e) => updatePrize(idx, { winnersCount: Math.max(1, Number(e.target.value)) })}
                                      className="w-full bg-[#f8f9fc] px-4 py-3.5 rounded-xl border border-[#e2e8f0] text-[12px] font-black text-indigo-600 outline-none text-center shadow-sm group-hover:border-[#cbd5e1] transition-colors"
                                    />
                                    <div className="absolute right-2 top-1/2 -translate-y-1/2 flex flex-col text-[#94a3b8]">
                                      <button onClick={() => updatePrize(idx, { winnersCount: (prize.winnersCount || 1) + 1 })} className="hover:text-indigo-600"><ChevronUp className="w-3 h-3" /></button>
                                      <button onClick={() => updatePrize(idx, { winnersCount: Math.max(1, (prize.winnersCount || 1) - 1) })} className="hover:text-indigo-600"><ChevronDown className="w-3 h-3" /></button>
                                    </div>
                                  </div>
                                </div>
                              )}

                              <div className="w-14 shrink-0">
                                <label className="text-[10px] uppercase font-black text-[#64748b] block mb-2 tracking-widest text-center">COLOR</label>
                                <div className="relative h-[46px] w-full rounded-xl border border-[#e2e8f0] overflow-hidden bg-[#f8f9fc] group hover:border-[#cbd5e1] transition-colors">
                                  <input 
                                    type="color" 
                                    value={prize.color}
                                    onChange={(e) => updatePrize(idx, { color: e.target.value })}
                                    className="absolute inset-0 w-[150%] h-[150%] cursor-pointer -translate-x-1/4 -translate-y-1/4"
                                  />
                                </div>
                              </div>

                              <div className="flex justify-end lg:pb-1">
                                <button 
                                  onClick={() => removePrize(prize.id)}
                                  className="p-3 text-slate-300 hover:text-red-500 transition-all bg-white hover:bg-red-50 rounded-xl"
                                >
                                  <Trash2 className="w-5 h-5" />
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </div>
        )}

        {activeTab === 'preview' && (
          <div className="flex flex-col items-center justify-center py-10 sm:py-16 bg-slate-50 rounded-[48px] border-2 border-slate-100 shadow-inner">
             <div className="w-full max-w-2xl transform scale-110">
                <GameSimulator 
                  type={formData.type} 
                  prizes={formData.prizes} 
                  questions={formData.questions}
                  endDate={formData.endDate}
                  theme={formData.theme}
                  buttonText={formData.buttonText}
                  campaignId={formData.id}
                  projectId={formData.projectId}
                  campaignName={formData.name}
                />
             </div>
          </div>
        )}

        {activeTab === 'embed' && <EmbedCode campaignId={formData.id} projectId={formData.projectId} domain={domain} />}
      </div>
    </div>
  );
};

const TabButton: React.FC<{ active: boolean, onClick: () => void, icon: React.ReactNode, label: string }> = ({ active, onClick, icon, label }) => (
  <button 
    onClick={onClick}
    className={`flex items-center gap-3 px-8 py-5 text-[10px] font-black uppercase tracking-[0.15em] transition-all border-b-4 shrink-0 ${
      active ? 'border-indigo-600 text-indigo-600 bg-white' : 'border-transparent text-slate-400 hover:text-slate-600 hover:bg-slate-100/50'
    }`}
  >
    <span className={`${active ? 'text-indigo-600' : 'text-slate-300'}`}>{icon}</span>
    <span>{label}</span>
  </button>
);

export default CampaignEditor;
